"use client";
// src/app/complaints/page.tsx
import React, { useState } from 'react';
import { useToast } from '@/components/ui/toast';

export default function Complaints() {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = (form.querySelector('#email') as HTMLInputElement)?.value || '';
    const url = (form.querySelector('#url') as HTMLInputElement)?.value || '';
    const message = (form.querySelector('#message') as HTMLTextAreaElement)?.value || '';

    setSubmitting(true);
    try {
      const res = await fetch('/api/proxy/api/complaints', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, url: url || null, message }),
      });
      if (res.ok) {
        toast({ title: 'Complaint submitted', description: 'We will review it and respond to you.', variant: 'success' });
        setSubmitted(true);
        return;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch {
      // Mailto fallback
      const subject = encodeURIComponent('JuicyStarz Complaint / Report');
      const body = encodeURIComponent(`${message}\n\nReported URL: ${url || '(none)'}\nFrom: ${email}`);
      toast({ title: 'Could not submit online', description: 'Opening your email client as fallback.', variant: 'destructive' });
      try { window.location.href = `mailto:support@juicystarz.com?subject=${subject}&body=${body}`; } catch {}
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-2xl text-center">
        <h1 className="text-2xl font-semibold mb-2">Thank you</h1>
        <p className="text-sm text-muted-foreground">Your complaint has been received. We will review it and respond to the email you provided.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      <h1 className="text-2xl font-semibold mb-2">Complaints</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Use this form to report abuse, copyright concerns, or other issues. We review all submissions and aim to respond promptly.
      </p>
      <div className="rounded-md border p-3 mb-3 text-xs text-muted-foreground">
        Tip: For a specific post, image, or video, use the Report action on that content so we capture exact context.
      </div>
      <form className="space-y-3" onSubmit={onSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium">Your Email</label>
          <input id="email" name="email" type="email" required className="mt-1 w-full rounded-md border px-3 py-2 bg-card text-foreground" />
        </div>
        <div>
          <label htmlFor="url" className="block text-sm font-medium">URL (optional)</label>
          <input id="url" name="url" type="url" placeholder="https://example.com/page" className="mt-1 w-full rounded-md border px-3 py-2 bg-card text-foreground" />
        </div>
        <div>
          <label htmlFor="message" className="block text-sm font-medium">Message</label>
          <textarea id="message" name="message" required rows={5} className="mt-1 w-full rounded-md border px-3 py-2 bg-card text-foreground" />
        </div>
        <div className="pt-2">
          <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 font-medium disabled:opacity-50">
            {submitting ? 'Sending...' : 'Send'}
          </button>
        </div>
      </form>
      <div className="mt-3 text-xs text-muted-foreground">
        Or email us directly: <a href="mailto:support@juicystarz.com" className="underline">support@juicystarz.com</a>
      </div>
    </div>
  );
}
